/**
 * SkillsPage — Phase 9 dedicated /skills tab.
 *
 * Single sortable table over ALL preloaded skills (no top-N), powered by
 * the rewritten `computeSkillsUsage` (Plan 01) via the simplified
 * `useSkillsUsage(filters)` hook (Plan 02). Row click opens the shared
 * TraceDrillDownModal with `extras.preloadedSkill = row.topicId`,
 * filtering /api/traces/list to traces where this skill was preloaded
 * (Plan 01 backend predicate `raw_metadata -> 'preloadedSkills' ? $1`).
 *
 * Inside the modal, row expand is enabled (Plan 03 wiring) — the
 * expanded body shows full input + output (or error) plus feedback,
 * via TraceTable's existing LazyTraceDetail.
 *
 * The page reuses the global FilterBar via DashboardLayout — no per-tab
 * filter widgets. Sort is client-side (36 rows max in production).
 */

import { RecountNotice } from '@/components/RecountNotice';
import * as React from 'react';

import { useSkillsUsage } from '@/hooks/use-operations';
import { useUrlFilters } from '@/hooks/use-url-filters';
import { readTableSort, type TableSort } from '@/lib/filters-schema';
import { formatLatencyMs } from '@/lib/format';

import { Card, CardContent } from '@/components/ui/card';
import { SkillDetailModal } from '@/components/SkillDetailModal';
import { SortableTable, type SortableColumn } from '@/components/SortableTable';
import { TabErrorBoundary } from '@/components/TabErrorBoundary';
import { TraceDrillDownModal } from '@/components/TraceDrillDownModal';
import type { SkillUsageRow } from '@/services/api-types';

// Inline retry card — mirror of OperationsPage / OverviewPage / EngagementPage.
function ErrorRetry({
  label,
  onRetry,
}: {
  label: string;
  onRetry: () => void;
}): React.ReactElement {
  return (
    <Card data-testid={`${label}-error`}>
      <CardContent className="p-4 space-y-2">
        <div className="text-sm font-medium text-red-600">
          {label} failed to load
        </div>
        <button
          type="button"
          onClick={onRetry}
          className="text-xs underline text-primary hover:no-underline"
        >
          Retry
        </button>
      </CardContent>
    </Card>
  );
}

const SKILLS_TABLE_ID = 'skills';
const DEFAULT_SORT: TableSort = { column: 'preloads', dir: 'desc' };

/** Avg-rating cell formatter — null → em-dash, else 1 decimal (e.g. "4.2"). */
function formatAvgRating(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return v.toFixed(1);
}

/** Saved-hours cell formatter — `0` → em-dash, else 1 decimal + `h`
 *  (e.g. `12.4h`). Same `${n.toFixed(1)}h` shape as EngagementPage's
 *  TopUsers timeSavedHours column for visual parity. */
function formatSavedHours(v: number | null | undefined): string {
  if (v === null || v === undefined || v === 0) return '—';
  return `${v.toFixed(1)}h`;
}

/**
 * Client-side sort. The endpoint returns up to ~50 rows in production
 * (36 today), and SortableTable writes `sort[skills]` to the URL —
 * we read it here and apply a stable ordering before render. Same
 * pattern as OperationsPage's three tables. Null/undefined values
 * always sink to bottom regardless of dir.
 */
function sortRows(rows: SkillUsageRow[], sort: TableSort | undefined): SkillUsageRow[] {
  const eff = sort ?? DEFAULT_SORT;
  const key = eff.column as keyof SkillUsageRow;
  const dir = eff.dir === 'asc' ? 1 : -1;
  return rows.slice().sort((a, b) => {
    const av = a[key];
    const bv = b[key];
    const aMissing = av === null || av === undefined;
    const bMissing = bv === null || bv === undefined;
    if (aMissing && bMissing) return 0;
    if (aMissing) return 1;
    if (bMissing) return -1;
    if (typeof av === 'number' && typeof bv === 'number') {
      return (av - bv) * dir;
    }
    return String(av).localeCompare(String(bv)) * dir;
  });
}

export function SkillsPage(): React.ReactElement {
  const { filters } = useUrlFilters();
  const skills = useSkillsUsage(filters);

  // Row-click drill-down state. Carry both topicId (the click-through key
  // sent to the modal as extras.preloadedSkill) and displayName (the
  // human label rendered in the modal title). null = closed; setting a
  // non-null value opens the modal and triggers the inner useTracesList.
  const [drillDown, setDrillDown] = React.useState<
    { topicId: string; displayName: string } | null
  >(null);
  const handleClose = React.useCallback(() => setDrillDown(null), []);

  // Details-button state — independent of the trace drilldown above. Opening
  // SkillDetailModal triggers the lazy useSkillDetail fetch.
  const [skillDetail, setSkillDetail] = React.useState<
    { topicId: string; displayName: string } | null
  >(null);
  const handleSkillDetailClose = React.useCallback(
    () => setSkillDetail(null),
    [],
  );

  const sort = readTableSort(filters, SKILLS_TABLE_ID);
  const sortedRows = React.useMemo(
    () => sortRows(skills.data ?? [], sort),
    [skills.data, sort],
  );

  // Column order: Skill / Used / Saved time / Avg rating / Rated / Avg latency / Details
  // `topicId` is the row key but never rendered as text (K-04). The trailing
  // Details column is non-sortable and reuses `topicId` as a JSX trigger key.
  const columns: Array<SortableColumn<SkillUsageRow>> = React.useMemo(
    () => [
      { key: 'displayName', header: 'Skill', sortable: true },
      {
        key: 'preloads',
        header: 'Used',
        sortable: true,
        align: 'right',
        format: (v) => String(v ?? 0),
      },
      {
        key: 'savedHours',
        header: 'Saved time',
        sortable: true,
        align: 'right',
        format: (v) => formatSavedHours(v as number | null),
      },
      {
        key: 'avgRating',
        header: 'Avg rating',
        sortable: true,
        align: 'right',
        format: (v) => formatAvgRating(v as number | null),
      },
      {
        key: 'ratedCount',
        header: 'Rated',
        sortable: true,
        align: 'right',
        format: (v) => String(v ?? 0),
      },
      {
        key: 'avgLatencyMs',
        header: 'Avg latency',
        sortable: true,
        align: 'right',
        format: (v) => formatLatencyMs(v as number | null),
      },
      {
        // Non-data action column: opens SkillDetailModal. The row body
        // click still opens the trace drill-down modal — onClick on the
        // button stops propagation so the two interactions stay separate.
        key: 'topicId',
        header: '',
        sortable: false,
        align: 'right',
        format: (_v, row) => (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setSkillDetail({ topicId: row.topicId, displayName: row.displayName });
            }}
            className="text-xs underline underline-offset-2 text-primary hover:no-underline"
            aria-label={`Show ${row.displayName} details`}
            data-testid={`skill-details-${row.topicId}`}
          >
            Details
          </button>
        ),
      },
    ],
    [],
  );

  return (
    <section aria-labelledby="skills-heading" className="space-y-6">
      <header>
        <h1 id="skills-heading" className="text-2xl font-semibold">
          Skills
        </h1>
        <p className="text-sm text-muted-foreground">
          Which skills the bot preloads, how often, how fast, and how well
          they're rated. Click a row to inspect the underlying conversations.
        </p>
        <RecountNotice queries={[skills]} />
      </header>

      <TabErrorBoundary name="Skills usage">
        {skills.isError ? (
          <ErrorRetry
            label="Skills usage"
            onRetry={() => void skills.refetch()}
          />
        ) : (
          <div className="space-y-2">
            <SortableTable<SkillUsageRow>
              tableId={SKILLS_TABLE_ID}
              columns={columns}
              rows={sortedRows}
              defaultSort={DEFAULT_SORT}
              getRowKey={(r) => r.topicId}
              isLoading={skills.isLoading}
              emptyMessage="No skills preloaded in selected period"
              onRowClick={(row) =>
                setDrillDown({ topicId: row.topicId, displayName: row.displayName })
              }
              // Mobile: stacked card mirroring OperationsPage convention.
              // Show displayName as the primary label, never topicId.
              mobileRender={(row) => (
                <div className="space-y-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-medium text-foreground break-words">
                      {row.displayName}
                    </div>
                    <span className="shrink-0 text-sm font-semibold tabular-nums">
                      {row.preloads}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground tabular-nums">
                    saved {formatSavedHours(row.savedHours)}
                    {' · '}
                    avg {formatLatencyMs(row.avgLatencyMs)}
                    {' · '}
                    rating {formatAvgRating(row.avgRating)}
                    {row.ratedCount > 0 ? ` (n=${row.ratedCount})` : ''}
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSkillDetail({
                        topicId: row.topicId,
                        displayName: row.displayName,
                      });
                    }}
                    className="text-xs underline underline-offset-2 text-primary hover:no-underline"
                    aria-label={`Show ${row.displayName} details`}
                  >
                    Details
                  </button>
                </div>
              )}
            />
          </div>
        )}
      </TabErrorBoundary>

      {/* Phase 9 — row click drill-down. Inert when drillDown === null;
          the inner useTracesList stays disabled until the modal opens. */}
      <TraceDrillDownModal
        open={drillDown !== null}
        onClose={handleClose}
        filters={filters}
        extras={
          drillDown !== null
            ? { preloadedSkill: drillDown.topicId }
            : {}
        }
        title={
          drillDown !== null
            ? `Traces with skill "${drillDown.displayName}"`
            : ''
        }
        subtitle="Every trace where this skill was preloaded into the prompt for the current filters."
        testIdPrefix="skills-drilldown"
      />

      {/* Details-button modal — independent of the trace drilldown above.
          Shows the skill's source Confluence articles + full markdown
          content. Lazy: useSkillDetail only fires when topicId is non-null. */}
      <SkillDetailModal
        open={skillDetail !== null}
        onClose={handleSkillDetailClose}
        topicId={skillDetail?.topicId ?? null}
        fallbackDisplayName={skillDetail?.displayName}
      />
    </section>
  );
}
